// ============================================================
// SIE4-export — genererar en .se-fil från aggregerad månadsdata.
//
// Skapar ett verifikat per månad som sammanfattar summan:
//   - Intäkter  (konto 3010)  — per månad
//   - Kostnader (konto 4010)  — per månad
//   - Ingående moms (konto 2641) — per månad
//   - Utgående moms (konto 2611) — per månad
//
// Bokföringsbyråer kan läsa in SIE4 direkt i Fortnox/Visma osv.
//
// OBS: vår datamodell är aggregat per månad, ej verifikat. Därför
// blir exporten "en sammanfattande post per månad" snarare än
// specifika verifikat. Bra för saldokontroll, ej för detaljrevision.
// ============================================================

import type { MonthlyRow } from '../data/store'
import type { CloudReceipt } from './cloudStore'

interface ExportInput {
  companyName: string
  orgNr?: string
  year: number
  months: MonthlyRow[]
  receipts?: CloudReceipt[]
}

/** Format number without decimals as SIE uses 2-decimal. */
function fmtAmount(n: number): string {
  // SIE uses "-" for negative, point (not comma) for decimal
  return n.toFixed(2)
}

/** YYYYMMDD för SIE-datum */
function yyyymmdd(y: number, m: number, d: number): string {
  return `${y}${String(m + 1).padStart(2, '0')}${String(d).padStart(2, '0')}`
}

function lastDayOfMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate()
}

export function generateSie4(input: ExportInput): string {
  const { companyName, orgNr, year, months } = input

  const lines: string[] = []
  const push = (s: string) => lines.push(s)

  // Header
  push('#FLAGGA 0')
  push('#PROGRAM "bolag.linespotting.com" "1.0"')
  push('#FORMAT PC8')
  push(`#GEN ${yyyymmdd(year, new Date().getMonth(), new Date().getDate())}`)
  push('#SIETYP 4')
  if (orgNr) push(`#ORGNR ${orgNr}`)
  push(`#FNAMN "${companyName.replace(/"/g, '\\"')}"`)
  push(`#RAR 0 ${year}0101 ${year}1231`)
  push(`#RAR -1 ${year - 1}0101 ${year - 1}1231`)

  // Kontoplan (minimal)
  push('#KONTO 2611 "Utgående moms 25%"')
  push('#KONTO 2641 "Ingående moms 25%"')
  push('#KONTO 3010 "Försäljning"')
  push('#KONTO 4010 "Inköp material och varor"')
  push('#KONTO 1930 "Företagskonto"')

  // Ett verifikat per månad som har data
  let verNo = 1
  for (let i = 0; i < 12; i++) {
    const m = months[i]
    if (!m) continue
    const hasData = m.vatIn > 0 || m.vatOut > 0 || m.income > 0 || m.expense > 0
    if (!hasData) continue

    const verDate = yyyymmdd(year, i, lastDayOfMonth(year, i))
    push(`#VER "A" "${verNo}" ${verDate} "Summering ${monthName(i)} ${year}"`)
    push('{')

    // Debet/Kredit-logik (sammanfattande):
    //   Intäkt:   1930 D income  / 3010 K income   (plus 2611 K vatOut)
    //   Kostnad:  4010 D expense / 1930 K expense  (plus 2641 D vatIn)

    if (m.income > 0) {
      // Kundinbetalningar
      push(`   #TRANS 1930 {} ${fmtAmount(m.income + m.vatOut)} ${verDate} "Intäkt ${monthName(i)}"`)
      push(`   #TRANS 3010 {} ${fmtAmount(-m.income)} ${verDate} "Försäljning ${monthName(i)}"`)
      if (m.vatOut > 0) {
        push(`   #TRANS 2611 {} ${fmtAmount(-m.vatOut)} ${verDate} "Utgående moms ${monthName(i)}"`)
      }
    }

    if (m.expense > 0) {
      push(`   #TRANS 4010 {} ${fmtAmount(m.expense)} ${verDate} "Kostnader ${monthName(i)}"`)
      if (m.vatIn > 0) {
        push(`   #TRANS 2641 {} ${fmtAmount(m.vatIn)} ${verDate} "Ingående moms ${monthName(i)}"`)
      }
      push(`   #TRANS 1930 {} ${fmtAmount(-(m.expense + m.vatIn))} ${verDate} "Utbetalning ${monthName(i)}"`)
    }

    push('}')
    verNo++
  }

  return lines.join('\r\n') + '\r\n'
}

function monthName(i: number) {
  return ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'][i]
}

/** Trigga nedladdning i webbläsaren. */
export function downloadSie(filename: string, contents: string) {
  // SIE använder egentligen PC8 (CP437), men de flesta importerare accepterar UTF-8.
  // Alternativ: encoda om till CP437 om revisorn klagar.
  const blob = new Blob([contents], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
